import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiHome, FiUsers, FiAward, FiBell, FiTag, FiFileText, FiLogOut, FiMenu, FiX, FiSettings, FiMail, FiMessageSquare, FiChevronDown, FiChevronRight, FiBarChart2, FiShield, FiUser } from 'react-icons/fi';
import useOutClick from '../hooks/useOutClick';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const { admin, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const accRef = useOutClick(() => setAccountOpen(false));

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: FiHome },
    { name: 'Clients', href: '/users', icon: FiUsers },
    { name: 'Certificates', href: '/certificates', icon: FiAward },
    { name: 'Reminders', href: '/reminders', icon: FiBell },
    { name: 'Tags', href: '/tags', icon: FiTag },
    { name: 'Reports', href: '/reports', icon: FiBarChart2 },
    {
      name: 'Templates',
      icon: FiMail,
      submenu: [
        { name: 'Email Templates', href: '/templates/email', icon: FiMail },
        { name: 'SMS Templates', href: '/templates/sms', icon: FiMessageSquare }
      ]
    },
    ...(isSuperAdmin() ? [{ name: 'Audit Logs', href: '/audit-logs', icon: FiFileText }] : []),
    ...(isSuperAdmin() ? [{ name: 'Role Management', href: '/role-management', icon: FiShield }] : []),
    ...(isSuperAdmin() ? [{ name: 'Settings', href: '/settings', icon: FiSettings }] : [])
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    return location.pathname === href;
  };

  const isTemplateActive = () => {
    return location.pathname.startsWith('/templates');
  };

  const handleItemClick = (item) => {
    if (item.submenu) {
      setTemplatesOpen(!templatesOpen);
    } else {
      navigate(item.href);
      setSidebarOpen(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full sidebar-bckg">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <FiX className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-lg p-1 logo-container animate-fade-in" style={{ border: '2px solid white' }}>
                <img
                  src={"https://premierchoicecpr.com/wp-content/uploads/2022/02/cropped-Premier_Choice_CPR.cdr_1.cdr_final-removebg-preview.png"}
                  alt="Premier Choice CPR Logo"
                  className="w-full h-full object-contain logo-image"
                  style={{ filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' }}
                />
              </div>
              <span className="ml-3 text-white text-xl font-semibold">Premier Choice</span>
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;

                if (item.submenu) {
                  return (
                    <div key={item.name}>
                      <button
                        onClick={() => handleItemClick(item)}
                        className={`${isTemplateActive()
                            ? 'sidebar-item active'
                            : 'sidebar-item'
                          } group flex items-center px-3 py-3 text-base font-medium rounded-lg w-full transition-all duration-200 btn-animate`}
                      >
                        <div className="sidebar-icon-container mr-4 p-1.5 rounded-md bg-white/10">
                          <Icon className="sidebar-icon flex-shrink-0 h-5 w-5" />
                        </div>
                        {item.name}
                        {templatesOpen ? (
                          <FiChevronDown className="ml-auto h-4 w-4" />
                        ) : (
                          <FiChevronRight className="ml-auto h-4 w-4" />
                        )}
                      </button>
                      {templatesOpen && (
                        <div className="ml-6 mt-1 space-y-1">
                          {item.submenu.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <button
                                key={subItem.name}
                                onClick={() => {
                                  navigate(subItem.href);
                                  setSidebarOpen(false);
                                }}
                                className={`${isActive(subItem.href)
                                    ? 'sidebar-item active'
                                    : 'sidebar-item'
                                  } group flex items-center px-3 py-2 text-base font-medium rounded-lg w-full transition-all duration-200`}
                              >
                                <div className="sidebar-icon-container mr-4 p-1 rounded-md bg-white/10">
                                  <SubIcon className="sidebar-icon flex-shrink-0 h-4 w-4" />
                                </div>
                                {subItem.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      navigate(item.href);
                      setSidebarOpen(false);
                    }}
                    className={`${isActive(item.href)
                        ? 'sidebar-item active'
                        : 'sidebar-item'
                      } group flex items-center px-3 py-3 text-base font-medium rounded-lg w-full transition-all duration-200 btn-animate`}
                  >
                    <div className="sidebar-icon-container mr-4 p-1.5 rounded-md bg-white/10">
                      <Icon className="sidebar-icon flex-shrink-0 h-5 w-5" />
                    </div>
                    {item.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 sidebar-bckg shadow-xl">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-lg p-1 logo-container animate-fade-in" style={{ border: '2px solid white' }}>
                  <img
                    src={"https://premierchoicecpr.com/wp-content/uploads/2022/02/cropped-Premier_Choice_CPR.cdr_1.cdr_final-removebg-preview.png"}
                    alt="Premier Choice CPR Logo"
                    className="w-full h-full object-contain logo-image"
                    style={{ filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' }}
                  />
                </div>
                <span className="ml-3 text-white text-xl font-semibold">Premier Choice</span>
              </div>
              <nav className="mt-5 flex-1 px-2 space-y-1">
                {navigation.map((item) => {
                  const Icon = item.icon;

                  if (item.submenu) {
                    return (
                      <div key={item.name}>
                        <button
                          onClick={() => handleItemClick(item)}
                          className={`${isTemplateActive()
                              ? 'sidebar-item active'
                              : 'sidebar-item'
                            } group flex items-center px-3 py-3 text-sm font-medium rounded-lg w-full transition-all duration-200 btn-animate`}
                        >
                          <div className="sidebar-icon-container mr-3 p-1.5 rounded-md bg-white/10">
                            <Icon className="sidebar-icon flex-shrink-0 h-5 w-5" />
                          </div>
                          {item.name}
                          {templatesOpen ? (
                            <FiChevronDown className="ml-auto h-4 w-4" />
                          ) : (
                            <FiChevronRight className="ml-auto h-4 w-4" />
                          )}
                        </button>
                        {templatesOpen && (
                          <div className="ml-6 mt-1 space-y-1">
                            {item.submenu.map((subItem) => {
                              const SubIcon = subItem.icon;
                              return (
                                <button
                                  key={subItem.name}
                                  onClick={() => {
                                    navigate(subItem.href);
                                    setSidebarOpen(false);
                                  }}
                                  className={`${isActive(subItem.href)
                                      ? 'sidebar-item active'
                                      : 'sidebar-item'
                                    } group flex items-center px-3 py-2 text-sm font-medium rounded-lg w-full transition-all duration-200`}
                                >
                                  <div className="sidebar-icon-container mr-3 p-1 rounded-md bg-white/10">
                                    <SubIcon className="sidebar-icon flex-shrink-0 h-4 w-4" />
                                  </div>
                                  {subItem.name}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={item.name}
                      onClick={() => navigate(item.href)}
                      className={`${isActive(item.href)
                          ? 'sidebar-item active'
                          : 'sidebar-item'
                        } group flex items-center px-3 py-3 text-sm font-medium rounded-lg w-full transition-all duration-200 btn-animate`}
                    >
                      <div className="sidebar-icon-container mr-3 p-1.5 rounded-md bg-white/10">
                        <Icon className="sidebar-icon flex-shrink-0 h-5 w-5" />
                      </div>
                      {item.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 min-h-0">
        {/* Top bar */}
        <div className="relative z-10 flex-shrink-0 flex h-20 px-3 bg-white shadow-lg border-b border-gray-100">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 hover:text-brand-primary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-primary md:hidden transition-colors duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0">
                <div className="flex items-center">
                  <div className="md:hidden w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-3 shadow-lg p-1">
                    <img
                      src={"https://premierchoicecpr.com/wp-content/uploads/2022/02/cropped-Premier_Choice_CPR.cdr_1.cdr_final-removebg-preview.png"}
                      alt="Premier Choice CPR Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Admin Dashboard
                  </h1>
                </div>
              </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              <div className="flex items-center space-x-4">
                <div className="flex flex-col text-right">
                  <span className="text-sm font-medium text-gray-900">{admin?.email}</span>
                  <span className="text-xs text-gray-500 capitalize">{admin?.role.role_name ? admin?.role?.role_name : admin?.role?.replace('_', ' ') ?? "N/A"}</span>
                </div>
                <button
                  onClick={() => setAccountOpen(!accountOpen)}
                  className="bg-white p-2 rounded-lg text-gray-400 hover:text-brand-primary hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate shadow-sm"
                >
                  <FiUser className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {
            accountOpen && (
              <div ref={accRef} className="absolute z-20 top-20 right-7 w-80 p-4 bg-white rounded-xl shadow-lg">
                <div className="flex flex-row items-center space-x-4">
                  <button
                    className="bg-white p-2 rounded-lg text-gray-400 hover:text-brand-primary hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate shadow-sm"
                  >
                    <FiUser className="h-6 w-6" />
                  </button>
                  <div className="flex flex-col text-left">
                    <span className="text-sm font-medium text-gray-900">{admin?.email}</span>
                    <span className="text-xs text-gray-500 capitalize">{admin?.role.role_name ? admin?.role?.role_name : admin?.role?.replace('_', ' ') ?? "N/A"}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-3 border-t border-t-gray-100 pt-5">
                  <a href="/audit-logs">
                    <div className="flex flex-row gap-4 justify-between items-center rounded-md cursor-pointer text-gray-400 hover:text-brand-primary">
                      <div className="bg-white p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate shadow-sm">
                        <FiFileText className="h-6 w-6" />
                      </div>
                      <p className="flex-1 text-sm font-normal">
                        Audit Log
                      </p>
                    </div>
                  </a>
                  <a href="/reports">
                    <div className="flex flex-row gap-4 justify-between items-center rounded-md cursor-pointer text-gray-400 hover:text-brand-primary">
                      <div className="bg-white p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate shadow-sm">
                        <FiBarChart2 className="h-6 w-6" />
                      </div>
                      <p className="flex-1 text-sm font-normal">
                        Reports
                      </p>
                    </div>
                  </a>
                  <div className="flex flex-row gap-4 justify-between items-center rounded-md cursor-pointer text-gray-400 hover:text-brand-primary" onClick={() => handleLogout()}>
                    <div className="bg-white p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate shadow-sm">
                      <FiLogOut className="h-6 w-6" />
                    </div>
                    <p className="flex-1 text-sm font-normal">
                      Log Out
                    </p>
                  </div>
                </div>
              </div>
            )
          }
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-gray-50 animate-fade-in page-transition">
          <div className="min-h-full pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;